import { useEffect, useMemo } from "react";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Typography } from "@mui/material";
import {
  flattenItems,
  getEmptyModel,
  wrapFilepickerValuesForSubmit,
} from "../../MDDForm/helper.js";
import { processTransformations } from "../../MDDForm/transformations.js";
import { buildFormSchema } from "./buildFormSchema.js";
import {
  selectedCoverageKeys,
  validateCoverageSteps,
} from "./validateCoverageSteps.js";
import {
  COVERAGE_TYPES_FIELD,
  coverageObjektSparten,
  firstSelectedObjektSparte,
} from "./coverageSelection.js";
import {
  FormActionsContext,
  FormCoverageContext,
  FormAttachmentsContext,
  FormEditModeContext,
  FormFlatItemsContext,
  FormModelContext,
  FormProgressModeContext,
  FormSubmitHandlerContext,
  useFormCoverage,
} from "./contexts.js";
import FormDefinitionRenderer from "./FormDefinitionRenderer.jsx";
import { api } from "@accredo/fe-shared/services/dataService.js";
import { useSessionFiles } from "../controls/FilePicker/useSessionFiles.js";
import { useClaimAttachmentFiles } from "../controls/FilePicker/useClaimAttachmentFiles.js";
import { prepareForSubmission } from "../../claim/costs/ClaimCostsEditor.jsx";
import { annotateCostEditors, costsOfEditor } from "./costEditorScopes.js";

const EMPTY_FORM_DEFINITION = [];
const EMPTY_OBJEKT_SPARTEN = [];
const NO_COVERAGE = {
  enabled: false,
  coverageTypes: [],
  objektSparten: EMPTY_OBJEKT_SPARTEN,
  validity: null,
};
const formDefinitionKeys = new WeakMap();
let nextFormDefinitionKey = 0;

// Stable numeric key per formdefinition array identity (React keys must be
// primitives - an array would be string-coerced and never change).
function getFormDefinitionKey(formDefinition) {
  if (!formDefinitionKeys.has(formDefinition)) {
    formDefinitionKeys.set(formDefinition, nextFormDefinitionKey++);
  }
  return formDefinitionKeys.get(formDefinition);
}

// Callers often mount the form with a placeholder definition and swap in the
// real one once it's fetched. Re-running reset() in place on a definition
// change leaves stale, unrevalidated errors behind (e.g. on `required_when`
// fields), so the form is remounted instead: every definition gets a fresh
// react-hook-form instance whose defaults, transformIn and schema all come
// from that definition. Keyed on the formdefinition array rather than `def`,
// since some callers build the `def` wrapper object inline on every render.
export default function NewFormRenderer(props) {
  return (
    <FormRendererInstance
      key={getFormDefinitionKey(
        props.def.formdefinition ?? EMPTY_FORM_DEFINITION
      )}
      {...props}
    />
  );
}

function FormRendererInstance({
  def,
  model = {},
  editMode = true,
  actions = [],
  onDirtyChange,
  progressMode = "lax",
  objektSparten = EMPTY_OBJEKT_SPARTEN,
}) {
  // Sections are flattened server-side (ClaimFormSectionMaterializer); the
  // renderer only ever sees a plain inline formdefinition.
  const formDefinition = useMemo(
    () => annotateCostEditors(def.formdefinition ?? []),
    [def]
  );
  const flatItems = useMemo(
    () => flattenItems(formDefinition),
    [formDefinition]
  );
  const schema = useMemo(() => buildFormSchema(flatItems), [flatItems]);
  const emptyModel = useMemo(
    () => getEmptyModel(formDefinition),
    [formDefinition]
  );

  const {
    sessionFiles: sessionAttachmentFiles,
    addSessionFile: addSessionAttachmentFile,
    updateSessionFileDescription: updateSessionAttachmentFileDescription,
    getUuidForServerId,
    clearSessionFiles: clearSessionAttachments,
  } = useSessionFiles();
  const { files: claimAttachmentFiles } = useClaimAttachmentFiles({
    claimId: model?.id,
  });
  const attachmentsCtx = useMemo(
    () => ({
      claimAttachmentFiles,
      sessionAttachmentFiles,
      addSessionAttachmentFile,
      updateSessionAttachmentFileDescription,
      getUuidForServerId,
      clearSessionAttachments,
    }),
    [
      claimAttachmentFiles,
      sessionAttachmentFiles,
      addSessionAttachmentFile,
      updateSessionAttachmentFileDescription,
      getUuidForServerId,
      clearSessionAttachments,
    ]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: emptyModel,
  });

  useEffect(() => {
    const base = { ...emptyModel, ...(model ?? {}) };
    // The claim's cost rows arrive as one list; each editor shows its own scope.
    if (Array.isArray(model?.costs)) {
      flatItems
        .filter((item) => item.control === "claim_costs_editor")
        .forEach((item) => {
          base[item.name] = costsOfEditor(model.costs, item);
        });
    }
    const transformed = processTransformations(
      def.transformIn ?? [],
      base,
      flatItems
    );
    // An Objekt with a single coverage-typed Objektsparte has nothing to choose.
    if (def.coverage_types?.length && !base[COVERAGE_TYPES_FIELD]?.length) {
      const only = coverageObjektSparten(def.coverage_types, objektSparten);
      if (only.length === 1) base[COVERAGE_TYPES_FIELD] = [only[0].sparte_id];
    }
    // A field stored as null or copied from a missing source starts from its
    // empty value: the schema accepts null even for a required field.
    Object.keys(emptyModel).forEach((key) => {
      if (transformed[key] == null) transformed[key] = emptyModel[key];
    });
    methods.reset(transformed);
    if (progressMode === "full") {
      queueMicrotask(() => methods.trigger());
    }
  }, [model?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(methods.formState.isDirty);
  }, [methods.formState.isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const makeSubmitHandler = (action) => {
    const submit = async (data) => {
      const payload = processTransformations(
        def.transformOut ?? [],
        { ...data },
        flatItems
      );
      // Cost attachments uploaded in this session carry their client uuid
      // so the backend can verify ownership (see useSessionFiles).
      flatItems
        .filter((item) => item.control === "claim_costs_editor")
        .forEach((item) => {
          if (Array.isArray(payload[item.name])) {
            payload[item.name] = prepareForSubmission(
              payload[item.name],
              getUuidForServerId
            );
          }
        });
      // The backend only processes attachment fields in the
      // { __filepicker__, attachments } shape, so untouched fields still in
      // storage shape must be wrapped too.
      wrapFilepickerValuesForSubmit(payload, flatItems, getUuidForServerId);
      try {
        if (action.method === "callback") {
          await action.callback(payload);
        } else if (["post", "patch", "put", "delete"].includes(action.method)) {
          const response = await api[action.method](action.endpoint, payload);
          action.onSuccess?.(response.data);
        }
      } catch (error) {
        const serverErrors = error.response?.data?.errors;
        if (serverErrors) {
          Object.entries(serverErrors).forEach(([field, messages]) => {
            methods.setError(field, { type: "server", message: messages[0] });
          });
        } else {
          methods.setError("root", {
            message: error.response?.data?.message || "Unbekannter Fehler",
          });
        }
      }
    };
    // `validate: false` submits the current values as-is, e.g. to save a
    // partially filled draft - handleSubmit silently skips the callback
    // while any rule fails.
    return action.validate === false
      ? () => submit(methods.getValues())
      : methods.handleSubmit(submit);
  };

  const { isDirty, isValid } = methods.formState;
  const coverageEnabled = !!def.coverage_types?.length;
  const rootIsStepper = formDefinition.some(
    (item) => item.control === "stepper"
  );

  return (
    <FormProvider {...methods}>
      <FormModelContext.Provider value={model}>
        <FormEditModeContext.Provider value={editMode}>
          <FormActionsContext.Provider value={actions}>
            <FormSubmitHandlerContext.Provider value={makeSubmitHandler}>
              <FormProgressModeContext.Provider value={progressMode}>
                <FormFlatItemsContext.Provider value={flatItems}>
                  <FormAttachmentsContext.Provider value={attachmentsCtx}>
                    <CoverageProvider
                      enabled={coverageEnabled}
                      def={def}
                      objektSparten={objektSparten}
                    >
                      <form onSubmit={(e) => e.preventDefault()} noValidate>
                        <FormDefinitionRenderer fields={formDefinition} />
                        {methods.formState.errors.root && (
                          <Typography
                            color="error"
                            variant="body2"
                            sx={{ mt: 1 }}
                          >
                            {methods.formState.errors.root.message}
                          </Typography>
                        )}
                        {!rootIsStepper && actions.length > 0 && (
                          <ActionBar
                            actions={actions}
                            isDirty={isDirty}
                            isValid={isValid}
                            makeSubmitHandler={makeSubmitHandler}
                          />
                        )}
                      </form>
                    </CoverageProvider>
                  </FormAttachmentsContext.Provider>
                </FormFlatItemsContext.Provider>
              </FormProgressModeContext.Provider>
            </FormSubmitHandlerContext.Provider>
          </FormActionsContext.Provider>
        </FormEditModeContext.Provider>
      </FormModelContext.Provider>
    </FormProvider>
  );
}

// Subscribes to every field value, so it is only mounted for forms with
// coverage types.
function CoverageProvider({ enabled, def, objektSparten, children }) {
  const { control, setValue } = useFormContext();
  const values = useWatch({ control });
  const selected = firstSelectedObjektSparte(
    def.coverage_types,
    objektSparten,
    values[COVERAGE_TYPES_FIELD]
  );

  // `sparte_id` follows the selection: the first selected Objektsparte in
  // definition order.
  useEffect(() => {
    if (enabled && selected && values.sparte_id !== selected.sparte_id) {
      setValue("sparte_id", selected.sparte_id);
    }
  }, [enabled, selected, values.sparte_id, setValue]);

  const coverageCtx = useMemo(
    () =>
      enabled
        ? {
            enabled,
            coverageTypes: def.coverage_types,
            objektSparten,
            validity: validateCoverageSteps(
              def,
              values,
              selectedCoverageKeys(values[COVERAGE_TYPES_FIELD], objektSparten)
            ),
          }
        : { ...NO_COVERAGE, objektSparten },
    [enabled, def, objektSparten, values]
  );
  return (
    <FormCoverageContext.Provider value={coverageCtx}>
      {children}
    </FormCoverageContext.Provider>
  );
}

function ActionBar({ actions, isDirty, isValid, makeSubmitHandler }) {
  const { reset } = useFormContext();
  const coverage = useFormCoverage();

  const isSubmitEnabled = (action) => {
    if (action.enabled === "always") return true;
    if (action.enabled === "valid") return isValid;
    if (typeof action.enabled === "function")
      return action.enabled({ isDirty, isValid, coverage: coverage.validity });
    return isDirty && isValid;
  };

  return (
    <Box
      sx={{
        mt: 3,
        display: "flex",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      {actions.map((action, i) => {
        if (action.type === "cancel") {
          return (
            <Button key={i} onClick={() => reset()}>
              {action.label ?? "Abbrechen"}
            </Button>
          );
        }
        if (action.type === "submit") {
          return (
            <Button
              key={i}
              type="button"
              variant="contained"
              color={action.color ?? "primary"}
              disabled={!isSubmitEnabled(action)}
              onClick={makeSubmitHandler(action)}
            >
              {action.label ?? "Speichern"}
            </Button>
          );
        }
        return null;
      })}
    </Box>
  );
}
